import React, { Component } from "react";
import {
    Grid, Divider, LinearProgress, Button, IconButton, Tooltip, Chip,
    Dialog, DialogContent, Typography
} from "@material-ui/core";
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import FastForwardIcon from '@material-ui/icons/FastForward';
import UndoIcon from '@material-ui/icons/Undo';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import ScheduleIcon from '@material-ui/icons/Schedule';
import RefreshIcon from '@material-ui/icons/Refresh';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import CloseIcon from '@material-ui/icons/Close';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { injectIntl } from 'react-intl';
import { ProvisioningStatusBadge } from '../status-badge';
import SectionCard from '../section-card';
import ProvisioningErrorBanner from '../provisioning-error-banner';
import ForceDropConfirmDialog from '../force-drop-confirm-dialog';
import DangerConfirmDialog from '../danger-confirm-dialog';
import DeprovisionedTrialDetailDialog from '../deprovisioned-trial-detail-dialog';
import * as adminBillingService from '../../services/admin-billing.service';
import Api from "../../services/api";

// Catálogo de steps agora vem dinâmico do backend (GET /admin/provisioning/steps-catalog).
// Fonte de verdade: FieldService.SaaS.Business.Helpers.ProvisioningStepsCatalog.
// Frontend NÃO mantém mais nomes/descrições hardcoded — adicionar step novo no backend
// é suficiente; o painel renderiza automaticamente.

const ACTION_LABELS = {
    'retry': 'Reenviar provisionamento',
    'continue': 'Continuar de onde parou',
    'execute_step': 'Executar passo',
    'rollback_step': 'Rollback cascata',
    'reset_status': 'Status resetado manualmente',
    'deprovision_started': 'Desprovisionamento iniciado',
    'deprovision_forced_by_admin': 'Desprovisionamento forçado pelo admin',
    'drop_forced_by_admin': 'Drop do banco forçado pelo admin',
    'deprov_update_status': 'Status atualizado para "deprovisioning"',
    'deprov_teardown_k8s': 'Teardown da infraestrutura K8s',
    'deprov_cancel_gateway': 'Assinatura cancelada no gateway',
    'deprov_soft_delete': 'Soft-delete das entidades SaaS',
    'deprov_enqueue_drop': 'Drop do banco enfileirado',
    'deprov_publish_drop_event': 'Evento de drop publicado (forçado)',
    'deprov_complete': 'Desprovisionamento concluído',
    'deprov_fail': 'Falha no desprovisionamento',
    'deprov_drop_database': 'DROP DATABASE executado',
    'deprov_drop_database_user': 'DROP USER executado',
    'deprov_drop_database_skipped': 'Drop pulado (outro contrato ativo)',
    'deprov_drop_complete': 'Drop finalizado',
    'deprov_drop_fail': 'Falha no drop',
    'deprovision_retry': 'Retry manual do desprovisionamento',
    'deprovision': 'Excluir trial (desprovisionar)',
};

const getStepStatusColor = (status) => {
    switch (status) {
        case 'completed': return '#4caf50';
        case 'running': return '#2196f3';
        case 'failed': return '#f44336';
        case 'rolled_back': return '#ff9800';
        case 'rolling_back': return '#ff9800';
        case 'rollback_failed': return '#d32f2f';
        case 'pending': return '#9e9e9e';
        case 'skipped': return '#bdbdbd';
        default: return '#9e9e9e';
    }
};

const getStepStatusIcon = (status) => {
    switch (status) {
        case 'completed': return <CheckCircleIcon style={{ color: '#4caf50', fontSize: 20 }} />;
        case 'running': return <HourglassEmptyIcon style={{ color: '#2196f3', fontSize: 20 }} />;
        case 'failed': return <ErrorIcon style={{ color: '#f44336', fontSize: 20 }} />;
        case 'rolled_back': return <UndoIcon style={{ color: '#ff9800', fontSize: 20 }} />;
        case 'rolling_back': return <HourglassEmptyIcon style={{ color: '#ff9800', fontSize: 20 }} />;
        case 'rollback_failed': return <ErrorIcon style={{ color: '#d32f2f', fontSize: 20 }} />;
        default: return <ScheduleIcon style={{ color: '#9e9e9e', fontSize: 20 }} />;
    }
};

/**
 * Painel unificado de detalhe do provisionamento.
 *
 * Modos de uso:
 *  A) Modo standalone (página admin-provisioning-detail):
 *     <ProvisioningDetailPanel provisioningId={id} ... />
 *  B) Modo embutido (aba Provisionamento de admin-trial-detail / admin-subscription-detail):
 *     <ProvisioningDetailPanel hirerId={x} contractId={y} ... />
 *     Resolve internamente o queue id via getTrialProvisioning(hirerId).
 *
 * Props:
 *  - provisioningId: int  (modo A)
 *  - hirerId, contractId: int  (modo B)
 *  - defaultEmail: string  (Force Drop dialog)
 *  - enablePolling: bool  (habilita polling após executar step — usado em subscription-detail)
 *  - onForceDropSuccess: () => void  (parent reage ao sucesso; ex: history.push)
 *  - onAfterAction: () => void  (parent reage após qualquer ação completada; ex: recarregar lista)
 *  - onNotify: (variant, msg) => void  (parent injeta toast; padrão Snackbar interno se ausente)
 *  - showBackButton: bool + onBack: () => void  (botão Voltar interno opcional)
 */
class ProvisioningDetailPanel extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            actionLoading: false,
            detail: null,
            deprovisioningStatus: null,
            confirmDialog: { open: false, title: '', message: '', action: null },
            // Force Drop
            forceDropOpen: false,
            forceDropSending: false,
            forceDropSubmitting: false,
            forceDropCodeSentState: null,
            forceDropError: null,
            // Zona de perigo (Desprovisionar) — agora com 2FA
            dangerDialogOpen: false,
            dangerLoading: false,
            dangerSendingCode: false,
            dangerCodeSentState: null,
            // Modal "Detalhes do desprovisionamento"
            deprovisionedDialogOpen: false,
            // Polling
            pollingStepOrder: null,
            pollingCount: 0,
            // Catálogo dinâmico de steps (carregado do backend no mount).
            stepsCatalog: [],
            stepsCatalogLoaded: false,
        };
        this._pollingInterval = null;
        this._loadedKey = null;
    }

    componentDidMount() {
        // Inicialmente loading=true; informa o parent pra Overlay padrão aparecer já no mount.
        if (this.state.loading && this.props.onLoadingChange) {
            this.props.onLoadingChange(true);
        }
        this.loadStepsCatalog();
        this.tryLoad();
    }

    // Carrega catálogo dinâmico de steps. Cache de uma chamada por mount; falha cai
    // pra fallback (renderiza usando step.stepname salvo do banco — sem descrição amigável).
    loadStepsCatalog = () => {
        adminBillingService.getProvisioningStepsCatalog()
            .then(r => {
                if (r?.data?.success) {
                    this.setState({ stepsCatalog: r.data.data || [], stepsCatalogLoaded: true });
                } else {
                    this.setState({ stepsCatalogLoaded: true });
                }
            })
            .catch(() => this.setState({ stepsCatalogLoaded: true }));
    }

    // Helper: encontra entrada do catálogo por order. Fallback se catálogo ainda não carregou.
    getCatalogEntry(order) {
        return this.state.stepsCatalog.find(e => (e.order ?? e.Order) === order);
    }

    getStepDescription(order, savedStepName = null) {
        const entry = this.getCatalogEntry(order);
        if (entry) return entry.description ?? entry.Description ?? savedStepName ?? `Step ${order}`;
        return savedStepName ?? `Step ${order}`;
    }

    getStepName(order, savedStepName = null) {
        const entry = this.getCatalogEntry(order);
        if (entry) return entry.stepname ?? entry.StepName ?? savedStepName ?? `Step ${order}`;
        return savedStepName ?? `Step ${order}`;
    }

    componentDidUpdate(prevProps, prevState) {
        const oldKey = this.computeKey(prevProps);
        const newKey = this.computeKey(this.props);
        if (oldKey !== newKey) {
            this.tryLoad();
        }
        // Espelha loading interno pro parent (Overlay padrão da página).
        // Combina loading (carga inicial) + actionLoading (ações tipo executar step,
        // rollback, retry, etc.) — ambos devem disparar o Overlay.
        const wasBusy = prevState.loading || prevState.actionLoading;
        const isBusy = this.state.loading || this.state.actionLoading;
        if (wasBusy !== isBusy && this.props.onLoadingChange) {
            this.props.onLoadingChange(isBusy);
        }
    }

    componentWillUnmount() {
        this.stopPolling();
    }

    computeKey(props) {
        if (props.provisioningId) return `id:${props.provisioningId}`;
        if (props.hirerId && props.contractId) return `hc:${props.hirerId}:${props.contractId}`;
        if (props.hirerId) return `h:${props.hirerId}`;
        return null;
    }

    tryLoad = () => {
        const key = this.computeKey(this.props);
        if (!key || key === this._loadedKey) return;
        this._loadedKey = key;
        this.loadAll();
    }

    // Resolve provisioningQueueId em qualquer um dos modos.
    resolveProvisioningId = async () => {
        if (this.props.provisioningId) return this.props.provisioningId;
        if (this.props.hirerId) {
            try {
                const r = await adminBillingService.getTrialProvisioning(this.props.hirerId);
                if (r.data.success && r.data.data) return r.data.data.id;
            } catch { /* ignore */ }
        }
        return null;
    }

    loadAll = async () => {
        this.setState({ loading: true });
        try {
            const id = await this.resolveProvisioningId();
            if (!id) {
                this.setState({ loading: false, detail: null });
                return;
            }
            const r = await adminBillingService.getProvisioningDetail(id);
            if (r.data.success && r.data.data) {
                const detail = r.data.data;
                this.setState({ loading: false, detail });
                if (this.props.onDetailLoaded) this.props.onDetailLoaded(detail);
                if (detail.hirerid && detail.contractid) {
                    this.loadDeprovisioningStatus(detail.hirerid, detail.contractid);
                }
            } else {
                this.setState({ loading: false });
                this.notify('error', r.data.errors?.[0] || 'Erro ao carregar provisionamento');
            }
        } catch (err) {
            this.setState({ loading: false });
            this.notify('error', err.response?.data?.errors?.[0] || 'Erro ao carregar provisionamento');
            Api.kickoff(err);
        }
    }

    loadDeprovisioningStatus = (hirerId, contractId) => {
        adminBillingService.getDeprovisioningStatus(hirerId, contractId)
            .then(r => {
                if (r.data.success) this.setState({ deprovisioningStatus: r.data.data });
            })
            .catch(() => { /* silencioso */ });
    }

    notify = (variant, msg) => {
        if (this.props.onNotify) this.props.onNotify(variant, msg);
    }

    handleSyncAndRefresh = () => {
        const id = this.state.detail?.id;
        if (!id) return;
        // Liga loading imediatamente — sem isso o Overlay só apareceria depois do sync retornar
        // (loadAll é chamado no .then). Garante feedback visual desde o clique.
        this.setState({ loading: true });
        adminBillingService.syncProvisioningStatus(id)
            .then(() => this.loadAll())
            .catch(() => this.loadAll());
    }

    openConfirm = (title, message, action) => {
        this.setState({ confirmDialog: { open: true, title, message, action } });
    }

    closeConfirm = () => {
        this.setState({ confirmDialog: { open: false, title: '', message: '', action: null } });
    }

    handleAction = (serviceCall, successMsg, opts = {}) => {
        this.setState({ actionLoading: true, confirmDialog: { open: false } });
        serviceCall
            .then(result => {
                if (result.data.success) {
                    this.setState({ actionLoading: false });
                    this.notify('success', successMsg);
                    setTimeout(() => this.loadAll(), 1500);
                    if (opts.startPollingStep && this.props.enablePolling) {
                        this.startPolling(opts.startPollingStep);
                    }
                    if (this.props.onAfterAction) this.props.onAfterAction();
                } else {
                    this.setState({ actionLoading: false });
                    this.notify('error', result.data.errors?.[0] || 'Erro na operação');
                }
            })
            .catch(err => {
                this.setState({ actionLoading: false });
                this.notify('error', err.response?.data?.errors?.[0] || 'Erro na operação');
                Api.kickoff(err);
            });
    }

    startPolling = (stepOrder) => {
        this.stopPolling();
        this.setState({ pollingStepOrder: stepOrder, pollingCount: 0 });
        this._pollingInterval = setInterval(() => {
            this.setState(s => ({ pollingCount: s.pollingCount + 1 }));
            this.loadAll();
            // Para o polling se step não está mais running ou após 60 ticks (~5min)
            const steps = this.state.detail?.steps || [];
            const target = steps.find(s => s.steporder === stepOrder);
            if (!target || target.status !== 'running' || this.state.pollingCount > 60) {
                this.stopPolling();
            }
        }, 5000);
    }

    stopPolling = () => {
        if (this._pollingInterval) {
            clearInterval(this._pollingInterval);
            this._pollingInterval = null;
        }
        if (this.state.pollingStepOrder !== null) {
            this.setState({ pollingStepOrder: null, pollingCount: 0 });
        }
    }

    // ===== Action handlers =====

    handleContinue = () => {
        const intl = this.props.intl;
        this.openConfirm(
            intl.formatMessage({ id: 'admin.provisioning.continue.title' }),
            intl.formatMessage({ id: 'admin.provisioning.continue.confirm' }),
            () => this.handleAction(
                adminBillingService.continueProvisioning(this.state.detail.id),
                intl.formatMessage({ id: 'admin.provisioning.continue.success' })
            )
        );
    }

    handleRetry = () => {
        this.openConfirm(
            'Reenviar provisionamento',
            'O provisionamento será reenviado do início. Deseja continuar?',
            () => this.handleAction(
                adminBillingService.retryProvisioning(this.state.detail.id),
                'Provisionamento reenviado com sucesso'
            )
        );
    }

    handleResetStatus = () => {
        this.openConfirm(
            'Resetar status',
            'O status do provisionamento será resetado para "pending". Use quando o provisionamento ficou preso em "processing" sem progresso. Deseja continuar?',
            () => this.handleAction(
                adminBillingService.resetProvisioningStatus(this.state.detail.id),
                'Status resetado com sucesso'
            )
        );
    }

    handleRetryDeprovisioning = () => {
        this.openConfirm(
            'Reprocessar desprovisionamento',
            'O evento de desprovisionamento será republicado. O handler irá reprocessar de forma idempotente (operações já concluídas não serão repetidas). Deseja continuar?',
            () => this.handleAction(
                adminBillingService.retryDeprovisioning(this.state.detail.id),
                'Evento de desprovisionamento republicado com sucesso'
            )
        );
    }

    handleForceK8sCleanup = () => {
        // Lista de nomes resolvidos do step 12 (preview no diálogo).
        const steps = this.state.detail?.steps || [];
        const step12 = steps.find(s => s.steporder === 12);
        const namesPreview = [];
        if (step12 && step12.outputdata) {
            try {
                const parsed = typeof step12.outputdata === 'string' ? JSON.parse(step12.outputdata) : step12.outputdata;
                const manifests = parsed?.generatedManifests || parsed?.generatedmanifests || {};
                Object.values(manifests).forEach(yaml => {
                    if (typeof yaml !== 'string') return;
                    yaml.split('\n').forEach(line => {
                        const t = line.trim();
                        if (t.startsWith('name:')) {
                            const v = t.substring(5).trim().replace(/^"|"$/g, '');
                            if (v && namesPreview.indexOf(v) === -1) namesPreview.push(v);
                        }
                    });
                });
            } catch (_e) { /* ignora */ }
        }
        const namesText = namesPreview.length > 0
            ? `Recursos K8s a deletar (item-a-item por nome):\n• ${namesPreview.join('\n• ')}`
            : 'Não foi possível extrair os nomes do output_data do step 12 — o backend ainda fará a validação e pode rejeitar a operação.';

        this.openConfirm(
            'Limpar K8s',
            `Esta operação dispara cleanup ASSÍNCRONO no cluster Kubernetes para este contrato JÁ desprovisionado. ` +
            `A requisição é validada e enfileirada (resposta imediata 202); o trabalho roda no handler do RabbitMQ ` +
            `e o progresso aparece no histórico (linhas force_cleanup_k8s_*). NÃO toca em banco, contrato, hirer ou queue.\n\n` +
            namesText + '\n\nDeseja continuar?',
            () => this.handleAction(
                adminBillingService.forceK8sCleanup(this.state.detail.id),
                'Limpeza K8s enfileirada — acompanhe o progresso no histórico'
            )
        );
    }

    handleExecuteStep = (stepOrder) => {
        const intl = this.props.intl;
        this.openConfirm(
            intl.formatMessage({ id: 'admin.provisioning.executestep.title' }),
            intl.formatMessage({ id: 'admin.provisioning.executestep.confirm' }, { step: stepOrder, name: this.getStepDescription(stepOrder) }),
            () => this.handleAction(
                adminBillingService.executeProvisioningStep(this.state.detail.id, stepOrder),
                intl.formatMessage({ id: 'admin.provisioning.executestep.success' }),
                { startPollingStep: stepOrder }
            )
        );
    }

    handleRollbackStep = (stepOrder) => {
        const intl = this.props.intl;
        this.openConfirm(
            intl.formatMessage({ id: 'admin.provisioning.rollbackstep.title' }),
            intl.formatMessage({ id: 'admin.provisioning.rollbackstep.confirm' }, { step: stepOrder }),
            () => this.handleAction(
                adminBillingService.rollbackProvisioningStep(this.state.detail.id, stepOrder),
                intl.formatMessage({ id: 'admin.provisioning.rollbackstep.success' })
            )
        );
    }

    handleReportError = (stepOrder) => {
        this.openConfirm(
            'Reportar Erro',
            `Deseja reportar erro manualmente no passo ${stepOrder}? O passo sera marcado como falho e o provisionamento sera interrompido.`,
            () => this.handleAction(
                adminBillingService.reportProvisioningStepError(this.state.detail.id, stepOrder),
                'Erro reportado com sucesso'
            )
        );
    }

    // ===== Force Drop =====

    openForceDrop = () => {
        this.setState({ forceDropOpen: true, forceDropCodeSentState: null, forceDropError: null });
    }

    closeForceDrop = () => {
        this.setState({ forceDropOpen: false, forceDropCodeSentState: null, forceDropError: null });
    }

    handleSendForceDropCode = ({ email }) => {
        const { detail } = this.state;
        if (!detail?.hirerid) return;
        this.setState({ forceDropSending: true });
        adminBillingService.sendForceDropTwoFactorCode(detail.hirerid, email)
            .then(result => {
                if (result.data.success) {
                    this.setState({ forceDropSending: false, forceDropCodeSentState: { ...result.data.data, sent: true } });
                } else {
                    this.setState({ forceDropSending: false, forceDropCodeSentState: { sent: false, errormessage: result.data.errors?.[0] || 'Falha ao enviar código' } });
                }
            })
            .catch(err => {
                this.setState({ forceDropSending: false, forceDropCodeSentState: { sent: false, errormessage: 'Falha ao enviar código' } });
                Api.kickoff(err);
            });
    }

    handleConfirmForceDrop = (payload) => {
        const { detail } = this.state;
        if (!detail?.hirerid || !detail?.contractid) return;
        this.setState({ forceDropSubmitting: true, forceDropError: null });
        adminBillingService.forceDatabaseDrop(detail.hirerid, detail.contractid, payload)
            .then(result => {
                if (result.data.success) {
                    this.setState({ forceDropSubmitting: false, forceDropOpen: false, forceDropCodeSentState: null, forceDropError: null });
                    this.notify('success', 'Drop imediato executado. Aguardando confirmação no background.');
                    setTimeout(() => this.loadAll(), 800);
                    if (this.props.onForceDropSuccess) this.props.onForceDropSuccess();
                } else {
                    const msg = result.data.errors?.[0] || 'Falha ao forçar drop';
                    this.setState({ forceDropSubmitting: false, forceDropError: msg });
                }
            })
            .catch(err => {
                const msg = err.response?.data?.errors?.[0] || 'Falha ao forçar drop';
                this.setState({ forceDropSubmitting: false, forceDropError: msg });
                Api.kickoff(err);
            });
    }

    // ===== Zona de perigo: Desprovisionar =====

    handleSendScheduleDeprovCode = ({ email }) => {
        const { detail } = this.state;
        if (!detail?.hirerid) return;
        this.setState({ dangerSendingCode: true });
        adminBillingService.sendScheduleDeprovTwoFactorCode(detail.hirerid, email)
            .then(result => {
                if (result.data.success) {
                    this.setState({ dangerSendingCode: false, dangerCodeSentState: { ...result.data.data, sent: true } });
                } else {
                    this.setState({ dangerSendingCode: false, dangerCodeSentState: { sent: false, errormessage: result.data.errors?.[0] || 'Falha ao enviar código' } });
                }
            })
            .catch(err => {
                this.setState({ dangerSendingCode: false, dangerCodeSentState: { sent: false, errormessage: 'Falha ao enviar código' } });
                Api.kickoff(err);
            });
    }

    handleScheduleDeprovisioning = ({ reason, confirmWorkspace, twoFactorCode }) => {
        const { detail } = this.state;
        if (!detail?.hirerid || !detail?.contractid) return;
        // Decide endpoint baseado no modo de provisionamento (trial vs assinatura).
        const isTrial = (detail.provisioningmode || '').toLowerCase() === 'trial';
        const call = isTrial
            ? adminBillingService.scheduleTrialDeprovisioning
            : adminBillingService.scheduleSubscriptionDeprovisioning;

        this.setState({ dangerLoading: true });
        call(detail.hirerid, detail.contractid, { reason, confirmWorkspace, twoFactorCode })
            .then(result => {
                if (result.data.success) {
                    this.setState({ dangerLoading: false, dangerDialogOpen: false, dangerCodeSentState: null });
                    this.notify('success', 'Desprovisionamento agendado. O ambiente será removido após o período de carência.');
                    // Recarrega tudo pra refletir novo status (provisioningstatus muda pra deprovisioning_scheduled)
                    setTimeout(() => this.loadAll(), 800);
                    if (this.props.onAfterAction) this.props.onAfterAction();
                } else {
                    this.setState({ dangerLoading: false });
                    this.notify('error', result.data.errors?.[0] || 'Falha ao agendar desprovisionamento');
                }
            })
            .catch(err => {
                this.setState({ dangerLoading: false });
                this.notify('error', err.response?.data?.errors?.[0] || 'Falha ao agendar desprovisionamento');
                Api.kickoff(err);
            });
    }

    // ===== Helpers =====

    formatDuration(ms) {
        if (!ms) return '-';
        if (ms < 1000) return `${ms}ms`;
        if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
        return `${(ms / 60000).toFixed(1)}min`;
    }

    renderRefreshAction = (handler, disabled, title = 'Atualizar') => (
        <Tooltip title={title}>
            <span>
                <IconButton size="small" onClick={handler} disabled={disabled}>
                    <RefreshIcon />
                </IconButton>
            </span>
        </Tooltip>
    );

    // ===== Sub-renders =====

    renderStatusCard() {
        const { detail } = this.state;
        const { intl } = this.props;
        if (!detail) return null;

        const progress = detail.totalsteps > 0 ? (detail.currentstep / detail.totalsteps) * 100 : 0;
        // Em "partial" o erro normalmente fica no step (rolled_back/failed), não em detail.errormessage.
        // Deriva o effectiveError do step quebrado pra o banner aparecer também nesse cenário.
        const brokenStep = (detail.steps || []).find(s =>
            (s.status === 'failed' || s.status === 'rolled_back' || s.status === 'rollback_failed') && s.errormessage
        );
        const effectiveErrorMessage = detail.errormessage || brokenStep?.errormessage || null;
        const effectiveFailedStep = detail.failedstep || brokenStep?.steporder || null;
        const hasErrorBanner = (
            detail.status === 'failed'
            || detail.status === 'partial'
            || (detail.status || '').endsWith('_failed')
        ) && effectiveErrorMessage;

        return (
            <SectionCard
                context="provisioning"
                title={intl.formatMessage({ id: "admin.provisioning.detail" })}
                actions={
                    <>
                        {this.props.extraActions}
                        {this.state.deprovisioningStatus?.desprovisioningqueueid && (
                            <Tooltip title="Ver detalhes do desprovisionamento">
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => this.setState({ deprovisionedDialogOpen: true })}
                                    style={{ textTransform: 'none', marginRight: 8 }}
                                >
                                    Detalhes
                                </Button>
                            </Tooltip>
                        )}
                        {this.renderRefreshAction(this.handleSyncAndRefresh, this.state.loading, 'Atualizar e sincronizar')}
                    </>
                }
            >
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="textSecondary">{intl.formatMessage({ id: "name" })}</Typography>
                        <Typography variant="body1">{detail.companyname || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="textSecondary">Workspace</Typography>
                        <Typography variant="body1">{detail.workspace || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="textSecondary">Status</Typography>
                        <ProvisioningStatusBadge status={detail.status} />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider style={{ margin: '8px 0' }} />
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>
                            {intl.formatMessage({ id: "admin.provisioning.progress" })} — {intl.formatMessage({ id: "admin.provisioning.step" })} {detail.currentstep || 0} / {detail.totalsteps || '?'}
                        </Typography>
                        <LinearProgress variant="determinate" value={progress} style={{ height: 10, borderRadius: 5 }} />
                    </Grid>

                    {hasErrorBanner && (
                        <Grid item xs={12}>
                            <ProvisioningErrorBanner
                                status={detail.status}
                                errorMessage={effectiveErrorMessage}
                                failedStep={effectiveFailedStep}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="textSecondary">{intl.formatMessage({ id: "admin.provisioning.created" })}</Typography>
                        <Typography variant="body1">{detail.createdat ? new Date(detail.createdat).toLocaleString('pt-BR') : '-'}</Typography>
                    </Grid>
                    {detail.completedat && (
                        <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="textSecondary">{intl.formatMessage({ id: "admin.provisioning.completed" })}</Typography>
                            <Typography variant="body1">{new Date(detail.completedat).toLocaleString('pt-BR')}</Typography>
                        </Grid>
                    )}
                </Grid>
            </SectionCard>
        );
    }

    renderStepsCard() {
        const { detail, pollingStepOrder } = this.state;
        const { intl } = this.props;
        if (!detail) return null;

        const steps = detail.steps || [];
        // lastCompletedOrder + maxStepFromTotal: pra esconder Retry/Continue quando ja terminou tudo.
        // Backend tambem bloqueia (RetryProvisioningAsync chega a Notify "nada a reprocessar"),
        // mas esconder na UI evita o usuario clicar e ver erro generico.
        const lastCompletedOrder = steps
            .filter(s => s.status === 'completed')
            .reduce((m, s) => Math.max(m, s.steporder ?? s.stepOrder ?? 0), 0);
        const totalStepsExpected = detail.totalsteps ?? 0;
        const allStepsCompleted = totalStepsExpected > 0 && lastCompletedOrder >= totalStepsExpected;
        const canContinue = (detail.status === 'failed' || detail.status === 'partial') && !allStepsCompleted;
        const canRetry = (detail.status === 'pending' || detail.status === 'failed' || detail.status === 'partial') && !allStepsCompleted;
        const canReset = detail.status === 'processing' && !steps.some(s => s.status === 'running');
        const canRetryDeprov = detail.status === 'deprovisioning' || detail.status === 'deprovision_failed';
        const canForceDrop = this.state.deprovisioningStatus?.dropstatus === 'pending';
        const isDedicated = detail.provisioningmode === 'dedicated' || detail.provisioningmode === 'conversion';
        // Force-cleanup K8s: só em contratos JÁ desprovisionados em modo dedicated/conversion
        // (modos sem K8s dedicado não têm o que limpar). Backend valida tudo de novo (queue.status,
        // contract_status_id=9, hirer.deleted=true, output_data step 12 com manifests).
        const canForceK8sCleanup = detail.status === 'deprovisioned' && isDedicated;
        // Derivado de detail.totalsteps (mantido sincronizado pelo backend via SyncStatusAsync)
        // OU do catálogo (filtrado por modo). Adicionar step novo NÃO requer alterar este código.
        const maxStep = detail.totalsteps
            || (this.state.stepsCatalog.length > 0
                ? Math.max(...this.state.stepsCatalog
                    .filter(e => isDedicated || !(e.dedicatedonly ?? e.DedicatedOnly))
                    .map(e => e.order ?? e.Order))
                : (isDedicated ? 16 : 10));
        const hasRunningStep = steps.some(s => s.status === 'running');

        return (
            <SectionCard
                context="list"
                title={
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        {intl.formatMessage({ id: 'admin.provisioning.steps.title' })}
                        {(detail?.provisioningmode || '').toLowerCase() === 'conversion' && (
                            <Chip label="Conversão" size="small"
                                style={{ backgroundColor: '#7e57c2', color: '#fff', fontWeight: 600, fontSize: 11 }} />
                        )}
                        {(detail?.provisioningmode || '').toLowerCase() === 'conversion' && detail?.eventhasdedicatedplan && (
                            <Chip label="Dedicated" size="small"
                                style={{ backgroundColor: '#1565c0', color: '#fff', fontWeight: 600, fontSize: 11 }} />
                        )}
                    </span>
                }
                actions={
                    <>
                        {canReset && (
                            <Button variant="outlined" size="small" onClick={this.handleResetStatus}
                                style={{ textTransform: 'none', borderColor: '#ff9800', color: '#ff9800' }}>
                                Resetar status
                            </Button>
                        )}
                        {/* Em conversion: Reenviar permanece habilitado (republica intencao original
                            do SubscribeAsync, com triagem fina por step). Continuar fica DESABILITADO
                            pq nextStep ingenuo pode pousar em step destrutivo. */}
                        {canRetry && (
                            <Button variant="contained" size="small" startIcon={<PlayArrowIcon />}
                                onClick={this.handleRetry}
                                style={{ backgroundColor: '#4caf50', color: '#fff', textTransform: 'none' }}>
                                Reenviar provisionamento
                            </Button>
                        )}
                        {canContinue && (() => {
                            const isConv = (detail?.provisioningmode || '').toLowerCase() === 'conversion';
                            return isConv ? (
                                <Tooltip title="Continuar nao se aplica em modo conversao. Use Reenviar.">
                                    <span>
                                        <Button variant="contained" size="small" startIcon={<FastForwardIcon />}
                                            disabled
                                            style={{ backgroundColor: '#bdbdbd', color: '#fff', textTransform: 'none' }}>
                                            {intl.formatMessage({ id: 'admin.provisioning.continue' })}
                                        </Button>
                                    </span>
                                </Tooltip>
                            ) : (
                                <Button variant="contained" size="small" startIcon={<FastForwardIcon />}
                                    onClick={this.handleContinue}
                                    style={{ backgroundColor: '#2196f3', color: '#fff', textTransform: 'none' }}>
                                    {intl.formatMessage({ id: 'admin.provisioning.continue' })}
                                </Button>
                            );
                        })()}
                        {canRetryDeprov && (
                            <Button variant="contained" size="small" startIcon={<RefreshIcon />}
                                onClick={this.handleRetryDeprovisioning}
                                style={{ backgroundColor: '#7b1fa2', color: '#fff', textTransform: 'none' }}>
                                Reprocessar desprovisionamento
                            </Button>
                        )}
                        {canForceDrop && (
                            <Button variant="contained" size="small" startIcon={<DeleteForeverIcon />}
                                onClick={this.openForceDrop}
                                style={{ backgroundColor: '#c62828', color: '#fff', textTransform: 'none', fontWeight: 600 }}>
                                Forçar drop agora
                            </Button>
                        )}
                        {canForceK8sCleanup && (
                            <Button variant="outlined" size="small" startIcon={<DeleteForeverIcon />}
                                onClick={this.handleForceK8sCleanup}
                                title="Cleanup K8s pra contrato já desprovisionado (recursos órfãos no cluster)"
                                style={{ borderColor: '#c62828', color: '#c62828', textTransform: 'none' }}>
                                Limpar K8s
                            </Button>
                        )}
                    </>
                }
            >
                {(() => {
                    // Lista de orders a renderizar: vem do catálogo (filtrado por modo+maxStep).
                    // Fallback: se catálogo não carregou, deriva dos steps salvos no banco.
                    // Em queue manual (provisioning_mode='manual'): NAO mostra steps "fantasmas"
                    // do catalogo, so os que efetivamente rodaram no banco. Evita admin clicar
                    // em Play/Rollback de steps que nunca rodaram nesse ambiente (risco de quebrar
                    // ambiente manual — recriar DB, deletar hirer, etc).
                    const isDedicated = detail?.provisioningmode === 'dedicated' || detail?.provisioningmode === 'conversion';
                    const isManualQueue = (detail?.provisioningmode || '').toLowerCase() === 'manual';
                    let orders;
                    if (isManualQueue) {
                        orders = [...new Set(steps.map(s => s.steporder))].sort((a, b) => a - b);
                    } else if (this.state.stepsCatalog.length > 0) {
                        orders = this.state.stepsCatalog
                            .filter(e => {
                                const order = e.order ?? e.Order;
                                const dedicatedOnly = e.dedicatedonly ?? e.DedicatedOnly;
                                if (order > maxStep) return false;
                                if (!isDedicated && dedicatedOnly) return false;
                                return true;
                            })
                            .map(e => e.order ?? e.Order)
                            .sort((a, b) => a - b);
                    } else {
                        orders = [...new Set(steps.map(s => s.steporder))].sort((a, b) => a - b);
                    }
                    return orders.map(order => {
                    const savedStep = steps.find(s => s.steporder === order);
                    const status = savedStep ? savedStep.status : 'pending';
                    const hasError = savedStep && savedStep.errormessage;
                    const durationMs = savedStep ? savedStep.durationms : null;
                    const isCompleted = status === 'completed';
                    const isFailed = status === 'failed';
                    const isPolling = pollingStepOrder === order;

                    return (
                        <div key={order} style={{
                            display: 'flex', alignItems: 'center', padding: '8px 12px', marginBottom: 4,
                            borderRadius: 8, backgroundColor: isFailed ? '#fff3e0' : isCompleted ? '#f1f8e9' : isPolling ? '#e3f2fd' : '#fafafa',
                            border: `1px solid ${getStepStatusColor(status)}22`
                        }}>
                            <div style={{ marginRight: 12, display: 'flex', alignItems: 'center' }}>
                                {getStepStatusIcon(status)}
                            </div>
                            <div style={{ flex: 1 }}>
                                <Typography variant="body2" style={{ fontWeight: 500 }}>
                                    {order}. {this.getStepDescription(order, savedStep?.stepname)}
                                </Typography>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
                                    <Chip label={status} size="small"
                                        style={{ backgroundColor: getStepStatusColor(status), color: '#fff', height: 20, fontSize: 11 }} />
                                    {durationMs != null && (
                                        <Typography variant="caption" color="textSecondary">
                                            {this.formatDuration(durationMs)}
                                        </Typography>
                                    )}
                                    {savedStep && savedStep.retrycount > 0 && (
                                        <Typography variant="caption" color="textSecondary">
                                            ({savedStep.retrycount}x)
                                        </Typography>
                                    )}
                                    {isPolling && (
                                        <Typography variant="caption" style={{ color: '#1976d2' }}>
                                            (acompanhando…)
                                        </Typography>
                                    )}
                                </div>
                                {hasError && (
                                    <Typography variant="caption" style={{ color: '#c62828', display: 'block', marginTop: 2 }}>
                                        {savedStep.errormessage}
                                    </Typography>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: 4 }}>
                                {/* Em queue manual: NAO mostra play/rollback individuais.
                                    Admin deve usar a aba "Acoes Manuais de Passos" (whitelist
                                    + 2FA) — clicar Play em step nao-whitelist pode corromper
                                    o ambiente (Create DB, Insert Hirer, etc).

                                    Em conversion: aplica whitelist por step (mesma regra do
                                    backend AdminProvisioningService.ExecuteStepAsync):
                                      - Permitidos sempre: 5 (InsertContract), 6 (Migrations), 10 (Welcome)
                                      - Permitidos se contract tem dedicated: 11-16 (K8s + Welcome dedicated)
                                      - Demais bloqueados — ja rodaram no trial. */}
                                {(() => {
                                    const isConv = (detail?.provisioningmode || '').toLowerCase() === 'conversion';
                                    if (!isConv) return null;
                                    // Whitelist por sub-fluxo de conversion: shared usa step 10 (Welcome shared);
                                    // dedicated usa step 16 (Welcome dedicated) + K8s (11-15). 5/6 sao comuns.
                                    // Em dedicated, step 10 deve estar BLOQUEADO (welcome ja sai pelo 16).
                                    const hasDedicated = !!detail?.eventhasdedicatedplan;
                                    const allowedShared = [5, 6, 10];
                                    const allowedDedicated = [5, 6, 11, 12, 13, 14, 15, 16];
                                    const allowedList = hasDedicated ? allowedDedicated : allowedShared;
                                    const allowed = allowedList.includes(order);
                                    if (allowed) return null;
                                    // Diferencia o motivo do bloqueio pra mensagem mais util:
                                    //  - step 10 em dedicated: welcome ja sai pelo step 16
                                    //  - 11-16 em shared: K8s/welcome dedicated nao aplicam
                                    //  - 1-4, 7-9: ja rodaram no trial original
                                    const k8sSteps = [11, 12, 13, 14, 15, 16];
                                    let tooltipMsg;
                                    if (order === 10 && hasDedicated) {
                                        tooltipMsg = `Passo 10 (Welcome shared) nao se aplica em conversion dedicated — welcome eh enviado pelo passo 16.`;
                                    } else if (k8sSteps.includes(order) && !hasDedicated) {
                                        tooltipMsg = `Passo ${order} so roda em contrato com plano dedicated.`;
                                    } else {
                                        tooltipMsg = `Passo ${order} ja rodou no trial — re-executar pode corromper o ambiente.`;
                                    }
                                    return (
                                        <>
                                            {(status === 'pending' || status === 'failed' || status === 'rolled_back' || status === 'rollback_failed') && !hasRunningStep && (
                                                <Tooltip title={tooltipMsg}>
                                                    <span>
                                                        <IconButton size="small" disabled>
                                                            <PlayArrowIcon style={{ color: '#bdbdbd' }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                            {isCompleted && (
                                                <Tooltip title={tooltipMsg}>
                                                    <span>
                                                        <IconButton size="small" disabled>
                                                            <UndoIcon style={{ color: '#bdbdbd' }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </>
                                    );
                                })()}
                                {!isManualQueue
                                 && !((detail?.provisioningmode || '').toLowerCase() === 'conversion'
                                      && (() => {
                                          const hasDedicated = !!detail?.eventhasdedicatedplan;
                                          const allowedShared = [5, 6, 10];
                                          const allowedDedicated = [5, 6, 11, 12, 13, 14, 15, 16];
                                          const allowedList = hasDedicated ? allowedDedicated : allowedShared;
                                          const allowed = allowedList.includes(order);
                                          return !allowed;
                                      })())
                                 && (status === 'pending' || status === 'failed' || status === 'rolled_back' || status === 'rollback_failed') && !hasRunningStep && (
                                    <Tooltip title={intl.formatMessage({ id: 'admin.provisioning.executestep' })}>
                                        <IconButton size="small" onClick={() => this.handleExecuteStep(order)}>
                                            <PlayArrowIcon style={{ color: '#2196f3' }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {!isManualQueue
                                 && !((detail?.provisioningmode || '').toLowerCase() === 'conversion'
                                      && (() => {
                                          const hasDedicated = !!detail?.eventhasdedicatedplan;
                                          const allowedShared = [5, 6, 10];
                                          const allowedDedicated = [5, 6, 11, 12, 13, 14, 15, 16];
                                          const allowedList = hasDedicated ? allowedDedicated : allowedShared;
                                          const allowed = allowedList.includes(order);
                                          return !allowed;
                                      })())
                                 && isCompleted && (
                                    <Tooltip title={intl.formatMessage({ id: 'admin.provisioning.rollbackstep' })}>
                                        <IconButton size="small" onClick={() => this.handleRollbackStep(order)}>
                                            <UndoIcon style={{ color: '#ff9800' }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {status === 'running' && (
                                    <Tooltip title="Reportar erro">
                                        <IconButton size="small" onClick={() => this.handleReportError(order)}>
                                            <ReportProblemIcon style={{ color: '#f44336' }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                    );
                });
                })()}
            </SectionCard>
        );
    }

    renderHistoryCard() {
        const { detail } = this.state;
        const { intl } = this.props;
        if (!detail) return null;
        const history = detail.history || [];
        if (history.length === 0) return null;

        return (
            <SectionCard context="history" title={intl.formatMessage({ id: 'admin.provisioning.history.title' })} noPadding>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f5f5f5' }}>
                                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>{intl.formatMessage({ id: 'admin.provisioning.history.action' })}</th>
                                <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>{intl.formatMessage({ id: 'admin.provisioning.step' })}</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>{intl.formatMessage({ id: 'admin.provisioning.history.triggeredby' })}</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>{intl.formatMessage({ id: 'admin.provisioning.history.date' })}</th>
                                <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>RabbitMQ</th>
                                <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>{intl.formatMessage({ id: 'admin.provisioning.history.processed' })}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.map(h => (
                                <tr key={h.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '6px 12px' }}>{ACTION_LABELS[h.actiontype] || h.actiontype}</td>
                                    <td style={{ padding: '6px 12px', textAlign: 'center' }}>{h.steporder || '-'}</td>
                                    <td style={{ padding: '6px 12px' }}>{h.triggeredby || '-'}</td>
                                    <td style={{ padding: '6px 12px' }}>{h.triggeredat ? new Date(h.triggeredat).toLocaleString('pt-BR') : '-'}</td>
                                    <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                                        {h.rabbitmqpublished ? (
                                            <CheckCircleIcon style={{ color: '#4caf50', fontSize: 18 }} />
                                        ) : h.rabbitmqerror ? (
                                            <Tooltip title={h.rabbitmqerror}>
                                                <ErrorIcon style={{ color: '#f44336', fontSize: 18 }} />
                                            </Tooltip>
                                        ) : (
                                            <HourglassEmptyIcon style={{ color: '#9e9e9e', fontSize: 18 }} />
                                        )}
                                    </td>
                                    <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                                        {h.processed ? (
                                            <CheckCircleIcon style={{ color: '#4caf50', fontSize: 18 }} />
                                        ) : (
                                            <ScheduleIcon style={{ color: '#9e9e9e', fontSize: 18 }} />
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </SectionCard>
        );
    }

    renderDangerZone() {
        const { detail, deprovisioningStatus } = this.state;
        if (!detail) return null;

        const provStatus = deprovisioningStatus?.provisioningstatus;
        // Status terminal — desprovisionamento já concluído (sucesso ou falha definitiva).
        // Nada destrutivo a fazer aqui (retry de falha vive nas actions do status card).
        const isTerminal = provStatus === 'deprovisioned' || provStatus === 'deprovision_failed';
        // Em andamento (agendado ou processando) → esconde "Desprovisionar" mas pode mostrar "Forçar".
        const isInProgress = provStatus === 'deprovisioning_scheduled' || provStatus === 'deprovisioning';
        // Forçar deleção só faz sentido se há pedido/agendamento em andamento.
        const canForceDrop = provStatus === 'deprovisioning_scheduled' || deprovisioningStatus?.dropstatus === 'pending';
        const canSchedule = !isTerminal && !isInProgress;

        // Bloco inteiro some quando NÃO há nenhuma ação destrutiva possível —
        // contrato já desprovisionado/falhou, ou o force drop não se aplica.
        if (isTerminal) return null;
        if (!canSchedule && !canForceDrop) return null;

        return (
            <SectionCard
                context="danger"
                title="Zona de perigo"
                subtitle="Desprovisionar remove definitivamente o ambiente do contratante (infra dedicada quando aplicável, assinatura no gateway, registros SaaS). O banco de dados do contratante é preservado por 30 dias para possibilitar restauração, depois é deletado permanentemente."
            >
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {canSchedule && (
                        <Button
                            variant="contained"
                            startIcon={<DeleteForeverIcon />}
                            onClick={() => this.setState({ dangerDialogOpen: true })}
                            style={{
                                backgroundColor: '#d32f2f',
                                color: '#fff',
                                textTransform: 'none',
                                fontWeight: 600,
                                boxShadow: '0 2px 8px rgba(211,47,47,0.3)',
                            }}
                        >
                            Desprovisionar ambiente
                        </Button>
                    )}
                    {canForceDrop && (
                        <Button
                            variant="contained"
                            onClick={this.openForceDrop}
                            style={{
                                backgroundColor: '#b71c1c',
                                color: '#fff',
                                textTransform: 'none',
                                fontWeight: 700,
                                boxShadow: '0 2px 12px rgba(183,28,28,0.4)',
                                border: '2px solid #7f0000',
                            }}
                        >
                            ⚠ Forçar deleção imediata (2FA)
                        </Button>
                    )}
                </div>
            </SectionCard>
        );
    }

    render() {
        const { intl, defaultEmail } = this.props;
        const { detail, confirmDialog } = this.state;

        if (!detail && !this.state.loading) {
            return (
                <SectionCard context="provisioning">
                    <Typography variant="body2" color="textSecondary" style={{ padding: 16, textAlign: 'center' }}>
                        Nenhum provisionamento encontrado.
                    </Typography>
                </SectionCard>
            );
        }

        return (
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    {this.renderStatusCard()}
                    <div style={{ marginTop: 16 }}>{this.renderStepsCard()}</div>
                    <div style={{ marginTop: 16 }}>{this.renderHistoryCard()}</div>
                    <div style={{ marginTop: 16 }}>{this.renderDangerZone()}</div>
                </Grid>

                {/* Confirm dialog */}
                <Dialog open={confirmDialog.open} onClose={this.closeConfirm} maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: 12, overflow: 'hidden' } }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', backgroundColor: '#f5f7fa' }}>
                        <Typography variant="h6" style={{ fontWeight: 600, fontSize: 18 }}>{confirmDialog.title}</Typography>
                        <IconButton size="small" onClick={this.closeConfirm} style={{ color: '#9e9e9e' }}><CloseIcon fontSize="small" /></IconButton>
                    </div>
                    <Divider />
                    <DialogContent style={{ padding: '16px 24px' }}>
                        <Typography>{confirmDialog.message}</Typography>
                    </DialogContent>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 24px 16px', backgroundColor: '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
                        <Button onClick={this.closeConfirm} style={{ textTransform: 'none', fontWeight: 500 }}>{intl.formatMessage({ id: "cancel" })}</Button>
                        <Button variant="contained" color="primary" onClick={confirmDialog.action} style={{ textTransform: 'none', fontWeight: 600, borderRadius: 6 }}>
                            {intl.formatMessage({ id: "confirm" })}
                        </Button>
                    </div>
                </Dialog>

                {/* Force Drop dialog */}
                {detail && (
                    <ForceDropConfirmDialog
                        open={this.state.forceDropOpen}
                        onClose={this.closeForceDrop}
                        onSendCode={this.handleSendForceDropCode}
                        onConfirm={this.handleConfirmForceDrop}
                        workspace={detail.workspace}
                        defaultEmail={defaultEmail || ''}
                        sending={this.state.forceDropSending}
                        submitting={this.state.forceDropSubmitting}
                        codeSentState={this.state.forceDropCodeSentState}
                        submitError={this.state.forceDropError}
                    />
                )}

                {/* Modal "Detalhes do desprovisionamento" — mesmo modal usado nas listagens
                    de Trial/Subscription (aba Desprovisionados). Só monta se houver registro. */}
                {detail && this.state.deprovisioningStatus?.desprovisioningqueueid && (
                    <DeprovisionedTrialDetailDialog
                        open={this.state.deprovisionedDialogOpen}
                        desprovisioningQueueId={this.state.deprovisioningStatus.desprovisioningqueueid}
                        onClose={() => this.setState({ deprovisionedDialogOpen: false })}
                        onForceDropSuccess={() => { this.setState({ deprovisionedDialogOpen: false }); this.loadAll(); }}
                        onDataRefreshed={() => this.loadAll()}
                        defaultEmail={this.props.defaultEmail || ''}
                    />
                )}

                {/* Desprovisionar (Zona de perigo) — pede workspace + 2FA + motivo. */}
                {detail && (
                    <DangerConfirmDialog
                        open={this.state.dangerDialogOpen}
                        onClose={() => this.setState({ dangerDialogOpen: false, dangerCodeSentState: null })}
                        onConfirm={this.handleScheduleDeprovisioning}
                        workspace={detail.workspace || ''}
                        title="Desprovisionar ambiente"
                        message={`Agendar desprovisionamento de "${detail.companyname || detail.workspace}" (${detail.workspace}).`}
                        description="Após o período de carência, a infra será derrubada (K8s/EKS para dedicado), a assinatura no gateway será cancelada (sem reembolso), e os registros SaaS deletados. O banco de dados do contratante é preservado por 30 dias — depois, deletado permanentemente. Você pode abortar enquanto o desprovisionamento estiver agendado."
                        warningText="Essa ação não pode ser desfeita após o período de carência expirar."
                        confirmLabel="Agendar desprovisionamento"
                        loading={this.state.dangerLoading}
                        requireReason
                        requireTwoFactor
                        onSendCode={this.handleSendScheduleDeprovCode}
                        sendingCode={this.state.dangerSendingCode}
                        codeSentState={this.state.dangerCodeSentState}
                        defaultEmail={this.props.defaultEmail || ''}
                    />
                )}
            </Grid>
        );
    }
}

export default injectIntl(ProvisioningDetailPanel);
